import { useEffect, useState, useMemo, Fragment } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import API from "../api/axios";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  InputAdornment,
  Grid,
  Stack,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PlaceIcon from "@mui/icons-material/Place";
import WorkOutlineIcon from "@mui/icons-material/Work";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TuneIcon from "@mui/icons-material/Tune";
import { ACCENT } from "../theme";
import DateFilter from "../components/ui/DateFilter";
import RefreshButton from "../components/ui/RefreshButton";

const POSITION_STATUS_LABEL = {
  ACTIVE: "Open",
  ON_HOLD: "On Hold",
  CLOSED: "Closed",
};
const POSITION_STATUS_COLOR = {
  ACTIVE: "success",
  ON_HOLD: "warning",
  CLOSED: "default",
};
const posStatusLabel = (k) => POSITION_STATUS_LABEL[k] || "Open";
const posStatusColor = (k) => POSITION_STATUS_COLOR[k] || "success";
const STATUS_OPTIONS = ["ACTIVE", "ON_HOLD", "CLOSED"];

const STATUS_COLOR = {
  SHORTLISTED: "success",
  SELECTED: "info",
  L1_ROUND: "info",
  L2_ROUND: "primary",
  ON_HOLD: "warning",
  REJECTED: "error",
  PENDING: "default",
};
const candStatusLabel = (k) =>
  ({
    SHORTLISTED: "Shortlisted",
    SELECTED: "Selected",
    L1_ROUND: "L1 Round",
    L2_ROUND: "L2 Round",
    ON_HOLD: "On Hold",
    REJECTED: "Rejected",
    PENDING: "Pending",
  })[k] ||
  k ||
  "Pending";

const fmtDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

// Timezone-safe YYYY-MM-DD, matching the staff pages' date-filter behavior
const dateOnly = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const todayStr = (() => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
})();

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
// Calendar-day freshness: same date = Fresh, next day = 1d old, etc.
function freshness(job) {
  const raw = job.positionDate || job.createdAt;
  if (!raw) return { label: "—", color: "default", days: null };
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(raw)) / 86400000,
  );
  if (days <= 1) return { label: "Fresh", color: "success", days };
  if (days <= 3) return { label: "Aging", color: "warning", days };
  return { label: "Old", color: "error", days };
}

function candidateName(c) {
  const fn = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return fn || c.candidateName || "Unnamed";
}

export default function ClientPositions() {
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [jobCodeFilter, setJobCodeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const fetchAll = (spin = false) => {
    if (spin) setLoading(true);
    Promise.all([
      API.get("/client/positions").then((r) => setJobs(r.data)),
      API.get("/client/candidates").then((r) => setCands(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const candsByJob = useMemo(() => {
    const m = {};
    cands.forEach((c) => {
      if (!c.jobCode) return;
      (m[c.jobCode] = m[c.jobCode] || []).push(c);
    });
    return m;
  }, [cands]);

  const recruiterNames = (jobCode) => [
    ...new Set(
      (candsByJob[jobCode] || []).map((c) => c.recruiterName).filter(Boolean),
    ),
  ];
  const recruiterCount = (jobCode) => recruiterNames(jobCode).length;

  const jobCodes = useMemo(
    () => [...new Set(jobs.map((j) => j.jobCode).filter(Boolean))].sort(),
    [jobs],
  );

  const visible = useMemo(
    () =>
      jobs.filter((j) => {
        const sq = search.toLowerCase();
        const matchesSearch =
          !sq ||
          [
            j.companyName,
            j.jobCode,
            j.roleName,
            j.location,
            j.skills,
            j.domain,
          ].some((v) => v?.toLowerCase().includes(sq));
        const matchesJobCode = !jobCodeFilter || j.jobCode === jobCodeFilter;
        const matchesStatus =
          !statusFilter || (j.status || "ACTIVE") === statusFilter;
        const ds = dateOnly(j.positionDate || j.createdAt);
        const matchesDate =
          (!fromDate && !toDate) ||
          (ds && (!fromDate || ds >= fromDate) && (!toDate || ds <= toDate));
        return matchesSearch && matchesJobCode && matchesStatus && matchesDate;
      }),
    [jobs, search, jobCodeFilter, statusFilter, fromDate, toDate],
  );

  const clearFilters = () => {
    setJobCodeFilter("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
  };

  // Export the currently VISIBLE (filtered) positions to an Excel file —
  // same shape as the admin Positions export, minus internal-only fields.
  const exportToExcel = () => {
    const rows = visible.map((j) => ({
      "Job Code": j.jobCode,
      Date: fmtDate(j.positionDate || j.createdAt),
      Company: j.companyName,
      Role: j.roleName,
      Domain: j.domain,
      Skills: j.skills,
      "Relevant Skills": j.relevantSkills,
      "Mandatory Skills": j.mandatorySkills,
      Experience: j.experience,
      "Relevant Experience": j.relevantExperience,
      "Job Description": j.jobDescription,
      Salary: j.salary,
      Location: j.location,
      Vacancies: j.numberOfPositions,
      Status: posStatusLabel(j.status),
      Candidates: (candsByJob[j.jobCode] || []).length,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Positions");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `Positions_${todayStr}.xlsx`,
    );
    toast.success(
      `Exported ${rows.length} position${rows.length !== 1 ? "s" : ""}`,
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Loading positions…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Header with freshness legend */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          pl: { xs: 5, md: 0 },
        }}
      >
        <Box>
          <Typography variant="h4">Positions</Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.5, fontSize: 14 }}>
            {visible.length} of {jobs.length} positions you provided
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              display: { xs: "none", md: "flex" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              px: 2,
              py: 1,
            }}
          >
            {[
              { c: "success.main", l: "Fresh ≤1 day" },
              { c: "warning.main", l: "Aging 2-3 days" },
              { c: "error.main", l: "Old >3 days" },
            ].map((d) => (
              <Stack
                key={d.l}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: d.c,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {d.l}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <DateFilter
            from={fromDate}
            to={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
            isToday={fromDate === todayStr && toDate === todayStr}
            isAllTime={!fromDate && !toDate}
            onToday={() => {
              setFromDate(todayStr);
              setToDate(todayStr);
            }}
            onAllTime={() => {
              setFromDate("");
              setToDate("");
            }}
          />
          <RefreshButton onClick={() => fetchAll(true)} />
          {visible.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Export Excel
            </Button>
          )}
        </Stack>
      </Box>

      <TextField
        placeholder="Search job code, role, location, skills…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TuneIcon
            sx={{
              color: "text.secondary",
              display: { xs: "none", sm: "block" },
            }}
          />
          <TextField
            select
            size="small"
            label="Job Code"
            value={jobCodeFilter}
            onChange={(e) => setJobCodeFilter(e.target.value)}
            sx={{ minWidth: 150, flex: 1 }}
          >
            <MenuItem value="">All Job Codes</MenuItem>
            {jobCodes.map((jc) => (
              <MenuItem key={jc} value={jc}>
                {jc}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150, flex: 1 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {posStatusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
          {(jobCodeFilter || statusFilter || fromDate || toDate) && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {visible.length === 0 ? (
        <Paper sx={{ textAlign: "center", py: 7, px: 2 }}>
          <WorkOutlineIcon sx={{ fontSize: 40, color: ACCENT, mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>No positions found</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
            {search || jobCodeFilter || statusFilter || fromDate || toDate
              ? "Try clearing your filters or search."
              : "Positions you provide will appear here."}
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Job Code</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Skills</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>Relevant Exp</TableCell>
                  <TableCell>Job Description</TableCell>
                  <TableCell>Salary</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="center">Vacancies</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Freshness</TableCell>
                  <TableCell align="center">Candidates</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((j) => {
                  const fr = freshness(j);
                  const list = candsByJob[j.jobCode] || [];
                  const rc = recruiterCount(j.jobCode);
                  const isOpen = openRow === j.jobCode;
                  return (
                    <Fragment key={j.jobCode}>
                      <TableRow
                        hover
                        sx={{
                          "& > *": { borderBottom: "unset" },
                          cursor: "pointer",
                        }}
                        onClick={() => setOpenRow(isOpen ? null : j.jobCode)}
                      >
                        <TableCell>
                          <KeyboardArrowRightIcon
                            fontSize="small"
                            sx={{
                              transition: "transform .2s",
                              transform: isOpen ? "rotate(90deg)" : "none",
                              color: "text.secondary",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: "monospace", fontWeight: 700 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                bgcolor: `${fr.color}.main`,
                              }}
                            />
                            {j.jobCode}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtDate(j.positionDate || j.createdAt)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {j.companyName}
                        </TableCell>
                        <TableCell>{j.roleName || "—"}</TableCell>
                        <TableCell>{j.skills || "—"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.experience || "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.relevantExperience || "—"}
                        </TableCell>
                        <TableCell
                          sx={{ width: 260, minWidth: 260, maxWidth: 260 }}
                        >
                          {j.jobDescription ? (
                            <Tooltip title="Click the row to read the full description">
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  lineHeight: 1.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {j.jobDescription}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {j.salary || "—"}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <PlaceIcon
                              sx={{ fontSize: 14, color: "text.secondary" }}
                            />
                            {j.location || "—"}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          {j.numberOfPositions != null &&
                          j.numberOfPositions !== ""
                            ? j.numberOfPositions
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={posStatusLabel(j.status)}
                            color={posStatusColor(j.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtDate(j.createdAt)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtDate(j.updatedAt)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={fr.label}
                            color={fr.color}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={list.length}
                            color={list.length > 0 ? "primary" : "default"}
                            variant={list.length > 0 ? "filled" : "outlined"}
                            sx={{ fontWeight: 700, minWidth: 32 }}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail */}
                      <TableRow key={j.jobCode + "-detail"}>
                        <TableCell
                          sx={{
                            py: 0,
                            borderBottom: isOpen ? undefined : "none",
                          }}
                          colSpan={17}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                m: 1,
                                ml: 5,
                                p: 2,
                                bgcolor: "background.default",
                                borderRadius: 1.5,
                              }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    mb: 1,
                                  }}
                                >
                                  Position Details
                                </Typography>
                                <Grid container spacing={1.5}>
                                  {[
                                    { label: "Domain", value: j.domain },
                                    {
                                      label: "Relevant Skills",
                                      value: j.relevantSkills,
                                    },
                                    {
                                      label: "Mandatory Skills",
                                      value: j.mandatorySkills,
                                    },
                                  ].map((d) => (
                                    <Grid item xs={6} sm={4} key={d.label}>
                                      <Typography
                                        sx={{
                                          fontSize: 10,
                                          color: "text.secondary",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {d.label}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {d.value || "—"}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              {j.jobDescription && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "text.secondary",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.6,
                                      mb: 0.5,
                                    }}
                                  >
                                    Job Description
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      whiteSpace: "pre-wrap",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {j.jobDescription}
                                  </Typography>
                                </Box>
                              )}

                              <Typography
                                sx={{ fontWeight: 700, fontSize: 13, mb: 1.5 }}
                              >
                                Candidates for {j.roleName} @ {j.companyName} ·{" "}
                                {list.length} candidate
                                {list.length !== 1 ? "s" : ""} from {rc}{" "}
                                recruiter{rc !== 1 ? "s" : ""}
                              </Typography>

                              {list.length === 0 ? (
                                <Typography
                                  sx={{ color: "text.secondary", fontSize: 13 }}
                                >
                                  No candidates submitted for this position yet.
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Candidate</TableCell>
                                      <TableCell>Recruiter</TableCell>
                                      <TableCell>Current Company</TableCell>
                                      <TableCell>Experience</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {list.map((c) => (
                                      <TableRow key={c.id}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                          {candidateName(c)}
                                        </TableCell>
                                        <TableCell>
                                          {c.recruiterName || "—"}
                                        </TableCell>
                                        <TableCell>
                                          {c.companyName || "—"}
                                        </TableCell>
                                        <TableCell>
                                          {c.totalExperience ||
                                            c.experienceYears ||
                                            "—"}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={candStatusLabel(c.feedback)}
                                            color={
                                              STATUS_COLOR[c.feedback] ||
                                              "default"
                                            }
                                            variant="outlined"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
